import { Component, EventEmitter, Input, Output, forwardRef } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { LazyLoadEvent } from 'primeng/api';
import { DropdownLazyLoadEvent, DropdownModule } from 'primeng/dropdown';

@Component({
    selector: 'platform-dropdown',
    standalone: true,
    imports: [FormsModule, DropdownModule],
    template: `
        <span class="p-float-label">
            <p-dropdown
                [options]="options"
                [(ngModel)]="selectedItem"
                [placeholder]="placeholder"
                [showClear]="true"
                (onChange)="onSelectionChanged($event)"
                (onHide)="onDropdownClose.emit()"
                [virtualScroll]="virtualScroll && this.options.length > 5"
                [virtualScrollItemSize]="45"
                [virtualScrollOptions]="{
                    lazy: lazy,
                    onLazyLoad: handleLazyLoad.bind(this),
                    step: 10,
                }"
                [panelStyle]="lazyDropdownStyle"
                styleClass="w-full"
                [optionLabel]="optionLabel"
                [optionValue]="optionValue"
                appendTo="body"
                [filter]="filter"
                [disabled]="disabled"
            >
            </p-dropdown>
            <label>{{ label }}</label>
        </span>
    `,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => DropDownComponent),
            multi: true,
        },
    ],
    styles: [
        `
            :host {
                display: block;
            }
        `,
    ],
})
export class DropDownComponent implements ControlValueAccessor {
    @Input() options: any[] = [];
    @Input() placeholder: string = 'Select an option';
    @Input() label: string = 'Select an option';
    @Input() optionLabel: string = 'label';
    @Input() optionValue: string = '';
    @Input() filter: boolean = true;
    @Input() disabled: boolean = false;
    @Input() virtualScroll: boolean = false;
    @Input() lazy: boolean = false;
    lazyDropdownStyle: any;

    @Output() onSelectionChange = new EventEmitter<any>();
    @Output() onDropdownClose = new EventEmitter<void>();
    @Output() onLazyLoad = new EventEmitter<LazyLoadEvent>();

    selectedItem: any;

    // Methods for ControlValueAccessor
    onChange: any = () => {};
    onTouched: any = () => {};

    ngOnChanges() {
        if (this.options.length && this.lazy) {
            this.getPanelStyle();
        }
    }

    writeValue(value: any): void {
        this.selectedItem = value;
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    setDisabledState?(isDisabled: boolean): void {
        // Handle disabled state if needed
    }

    // Handle selection change and propagate value
    onSelectionChanged(event: any) {
        this.selectedItem = event.value;
        this.onChange(event.value);
        this.onSelectionChange.emit(event);
    }

    handleLazyLoad(event: DropdownLazyLoadEvent) {
        this.onLazyLoad.emit(event);
    }

    getPanelStyle() {
        const itemHeight = 45;
        const maxVisibleItems = 20;

        const count = this.options?.length || 0;

        if (count <= maxVisibleItems) {
            this.lazyDropdownStyle = {
                maxHeight: `${count * itemHeight}px`,
                overflowY: 'auto',
            };
        }

        this.lazyDropdownStyle = {
            maxHeight: `${maxVisibleItems * itemHeight}px`,
            overflowY: 'auto',
        };
    }
}
